import { useState } from 'react';
import { portailApi } from '../../api/resources';
import { fmt } from '../../lib/currency';
import { trf } from '../../lib/i18n';
import { lienAppel } from '../../lib/portable';
import { C } from '../../theme/palette';
import { alerte, arrondi, bouton, carte, champ, dateCourte, nombre, stockDe } from './styles';

/**
 * « ✅ À valider » : les commandes des ouvriers, que le chef ou le responsable
 * valide telles quelles, modifie (quantités, dans la limite du stock) ou
 * refuse en disant pourquoi. Validée, la commande part au magasin.
 *
 * @param {object} props propriétés
 * @param {object[]|null} props.commandes commandes du client, null pendant la lecture
 * @param {Object<string, object>} props.catalogue articles du catalogue, par référence
 * @param {string} props.devise devise des prix
 * @param {() => void} props.apres relit les commandes et le stock après une décision
 * @returns {import('react').ReactElement} la liste
 */
export function CommandesAValider({ commandes, catalogue, devise, apres }) {
  const [message, setMessage] = useState('');
  if (commandes === null) return <div style={{ ...carte, color: C.muted }}>Chargement…</div>;
  const aValider = commandes.filter((c) => c.peutValider);
  return (
    <>
      <h2 style={{ fontSize: 17, margin: '4px 0 8px' }}>
        {trf('🔔 Commandes à valider · {0}', aValider.length)}
      </h2>
      {message && (
        <div
          role="status"
          style={{ ...carte, background: '#E8F3EC', color: '#14532D', fontWeight: 700 }}
        >
          {message}
        </div>
      )}
      {aValider.length === 0 && (
        <div style={{ ...carte, textAlign: 'center', color: C.muted }}>
          Rien à valider pour l’instant.
        </div>
      )}
      {aValider.map((c) => (
        <CarteAValider
          key={c.id}
          commande={c}
          catalogue={catalogue}
          devise={devise}
          fait={(texte) => {
            setMessage(texte);
            apres();
          }}
        />
      ))}
      <div
        style={{
          background: '#E8F3EC',
          borderRadius: 12,
          padding: '10px 12px',
          fontSize: 12,
          color: '#14532D',
          lineHeight: 1.5,
        }}
      >
        Validée, la commande part au magasin avec son bon de commande. En refusant, vous écrivez
        pourquoi : l’ouvrier le voit.
      </div>
    </>
  );
}

/** Une commande à valider, avec ses trois gestes : refuser, modifier, valider. */
function CarteAValider({ commande: c, catalogue, devise, fait }) {
  const [mode, setMode] = useState('lecture');
  const [motif, setMotif] = useState('');
  const [quantites, setQuantites] = useState({});
  const [etat, setEtat] = useState({ enCours: false, erreur: '' });
  const lignes = c.lignes || [];
  // La commande retient déjà sa marchandise : ce qu'elle peut prendre, c'est
  // le stock qui reste, plus ce qu'elle a déjà.
  const maxDe = (l) => {
    const article = catalogue[l.reference];
    return article ? stockDe(article) + nombre(l.quantite) : nombre(l.quantite);
  };
  const quantite = (l, i) =>
    mode === 'modif' && (l.reference || i) in quantites
      ? quantites[l.reference || i]
      : nombre(l.quantite);
  const fixe = (l, i, q) =>
    setQuantites((p) => ({
      ...p,
      [l.reference || i]: Math.min(maxDe(l), Math.max(0, arrondi(Number(q) || 0))),
    }));
  const total =
    mode === 'modif'
      ? lignes.reduce((s, l, i) => s + quantite(l, i) * nombre(l.prixNet), 0)
      : c.total;

  const agit = async (appel, texte) => {
    setEtat({ enCours: true, erreur: '' });
    try {
      await appel();
      setEtat({ enCours: false, erreur: '' });
      fait(texte);
    } catch (e) {
      setEtat({ enCours: false, erreur: e?.message || 'Ça n’a pas marché. Réessayez.' });
    }
  };
  const valide = () => {
    if (mode !== 'modif') {
      return agit(
        () => portailApi.valide(c.id),
        trf('✓ Commande {0} validée : elle part au magasin.', c.numero),
      );
    }
    const ajustements = lignes
      .map((l, i) => ({ reference: l.reference, quantite: quantite(l, i) }))
      .filter((l) => l.reference);
    if (ajustements.every((l) => l.quantite <= 0)) {
      setEtat({
        enCours: false,
        erreur: 'Il ne reste aucun article : refusez plutôt la commande.',
      });
      return undefined;
    }
    return agit(
      () => portailApi.valide(c.id, ajustements),
      trf('✓ Commande {0} validée : elle part au magasin.', c.numero),
    );
  };
  const refuse = () => {
    if (!motif.trim()) {
      setEtat({ enCours: false, erreur: 'Écrivez pourquoi vous refusez : l’ouvrier le verra.' });
      return undefined;
    }
    return agit(
      () => portailApi.refuse(c.id, motif.trim()),
      trf('Commande {0} refusée.', c.numero),
    );
  };

  return (
    <article
      aria-label={c.numero}
      style={{ ...carte, border: '2px solid #D97706', display: 'flex', flexDirection: 'column' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <b style={{ fontSize: 16 }}>{c.numero}</b>
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: '#B45309',
            background: '#FFF7ED',
            borderRadius: 8,
            padding: '4px 8px',
          }}
        >
          À valider
        </span>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.6, marginTop: 6 }}>
        {c.chantier && (
          <div>
            🏗 <b>{c.chantierNumero ? c.chantierNumero + ' · ' + c.chantier : c.chantier}</b>
          </div>
        )}
        <div>
          👷 {c.demandeur}
          {c.date ? ' · ' + dateCourte(c.date) : ''}
        </div>
        {c.demandeurTelephone && (
          <div>
            📞 <a href={lienAppel(c.demandeurTelephone)}>{c.demandeurTelephone}</a>
          </div>
        )}
      </div>
      <div
        style={{
          borderTop: '1px solid ' + C.border,
          marginTop: 8,
          paddingTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 13,
        }}
      >
        {lignes.map((l, i) => {
          const q = quantite(l, i);
          const max = maxDe(l);
          return mode === 'modif' && l.reference ? (
            <div
              key={l.reference || i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                opacity: q <= 0 ? 0.5 : 1,
              }}
            >
              <span
                style={{ flex: 1, minWidth: 0, textDecoration: q <= 0 ? 'line-through' : 'none' }}
              >
                {l.designation}
                <span style={{ display: 'block', fontSize: 11.5, color: C.muted }}>
                  {trf('max. {0}', max)}
                </span>
              </span>
              <button
                aria-label={'Moins ' + l.designation}
                onClick={() => fixe(l, i, q - 1)}
                style={{ ...bouton(C.navy, false), padding: '6px 10px', minWidth: 44 }}
              >
                −
              </button>
              <input
                aria-label={'Quantité ' + l.designation}
                inputMode="decimal"
                value={q}
                onChange={(e) => fixe(l, i, e.target.value.replace(',', '.'))}
                style={{ ...champ, width: 54, padding: 6, textAlign: 'center', fontWeight: 800 }}
              />
              <button
                aria-label={'Plus ' + l.designation}
                onClick={() => fixe(l, i, q + 1)}
                disabled={q >= max}
                style={{
                  ...bouton(C.navy),
                  padding: '6px 10px',
                  minWidth: 44,
                  opacity: q >= max ? 0.35 : 1,
                }}
              >
                +
              </button>
            </div>
          ) : (
            <div
              key={l.reference || i}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
            >
              <span>
                {q} × {l.designation}
              </span>
              {l.prixNet != null && <b>{fmt(q * nombre(l.prixNet))}</b>}
            </div>
          );
        })}
        {total != null && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '1px solid ' + C.border,
              paddingTop: 4,
            }}
          >
            <span>Total</span>
            <b>
              {devise} {fmt(total)}
            </b>
          </div>
        )}
      </div>
      {mode === 'refus' && (
        <label style={{ fontSize: 12, fontWeight: 800, color: C.grey, marginTop: 10 }}>
          Pourquoi refusez-vous ? (l’ouvrier le verra)
          <textarea
            rows={2}
            maxLength={300}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            style={{ ...champ, marginTop: 4, resize: 'vertical' }}
          />
        </label>
      )}
      {etat.erreur && (
        <div role="alert" style={alerte}>
          {etat.erreur}
        </div>
      )}
      {mode === 'lecture' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 6,
            marginTop: 10,
          }}
        >
          <button
            onClick={() => setMode('refus')}
            disabled={etat.enCours}
            style={{ ...bouton(C.grey, false), padding: '11px 4px', fontSize: 13 }}
          >
            Refuser
          </button>
          <button
            onClick={() => {
              setQuantites({});
              setMode('modif');
            }}
            disabled={etat.enCours}
            style={{ ...bouton(C.navy, false), padding: '11px 4px', fontSize: 13 }}
          >
            ✎ Modifier
          </button>
          <button
            onClick={valide}
            disabled={etat.enCours}
            style={{ ...bouton(C.green), padding: '11px 4px', fontSize: 13 }}
          >
            ✓ Valider
          </button>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 6,
            marginTop: 10,
          }}
        >
          <button
            onClick={() => {
              setMode('lecture');
              setEtat({ enCours: false, erreur: '' });
            }}
            disabled={etat.enCours}
            style={{ ...bouton(C.grey, false), padding: '11px 4px', fontSize: 13 }}
          >
            Annuler
          </button>
          {mode === 'refus' ? (
            <button
              onClick={refuse}
              disabled={etat.enCours}
              style={{ ...bouton(C.red), padding: '11px 4px', fontSize: 13 }}
            >
              Confirmer le refus
            </button>
          ) : (
            <button
              onClick={valide}
              disabled={etat.enCours}
              style={{ ...bouton(C.green), padding: '11px 4px', fontSize: 13 }}
            >
              ✓ Valider ces quantités
            </button>
          )}
        </div>
      )}
    </article>
  );
}
